import { LeftOutlined } from '@ant-design/icons'
import Actions from 'actions'
import { getNominative } from 'common/utils'
import { ResidentQuoteBox } from 'components/carePlans'
import InitialAssessmentCompleteForm from 'components/forms/screens/InitialAssessmentCompleteForm'
import { Spinner } from 'components/generic'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import { find } from 'lodash'
import Operations from 'operations'
import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { history } from 'store'
import {
  clearInitialAssessmentFlowStartedAt,
  getInitialAssessmentFlowDurationMs
} from './initialAssessmentFlowTiming'
import { Components } from './ResidentInitialAssessmentCompleteScreen.styled'

const FORM_ID = 'completeInitialAssessment'

const getResidentBoxText = (carePlanNominative: string) =>
  `You can now finalise my Initial Assessment, which will create my ${getNominative(
    carePlanNominative,
    'Care Plan'
  )}.`

export const ResidentInitialAssessmentCompleteScreen = () => {
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const sendEvent = useMixpanelEvent()

  const form = useSelector((state: any) => state.forms[FORM_ID])
  const resident = useSelector((state: any) =>
    find(state.data.residents, { id })
  )
  const features = useSelector((state: any) => state.global.home)
  const carePlanNominative = useSelector(
    (state: any) => state.global.home.preferredCarePlansNominative
  )
  const preferredPeopleNominative = useSelector(
    (state: any) => state.global.home.preferredPeopleNominative
  )

  useEffect(() => {
    if (resident) {
      dispatch(Actions.addForm({ data: { resident }, formId: FORM_ID }))
    }
  }, [dispatch, resident])

  const handleComplete = useCallback(() => {
    if (!id) return
    const residentId = resident?.id ?? id
    sendEvent(MixpanelEvents.INITIAL_ASSESSMENT_COMPLETED, {
      durationMs: getInitialAssessmentFlowDurationMs(residentId),
      residentId
    })
    clearInitialAssessmentFlowStartedAt(residentId)
    dispatch(Operations.loadListItem('residents', id))
    history.push(`/residents/${id}/care-plans`)
  }, [dispatch, id, resident?.id, sendEvent])

  // Guard against double submission: createCarePlan has no server-side duplicate
  // check, so a second click before the response mints a full duplicate set of
  // care plans (CM-4170).
  const isSubmitting = Boolean(form?.isSubmitting)

  const handleSubmit = useCallback(() => {
    if (!id || isSubmitting) return
    dispatch(
      Operations.postItem({
        url: `list/residents/${id}/createCarePlan`,
        onComplete: handleComplete,
        formId: FORM_ID,
        method: 'POST'
      })
    )
  }, [dispatch, handleComplete, id, isSubmitting])

  if (!features || !id || !resident) {
    return <Spinner />
  }

  const disabled = !form?.fields?.residentAccepted || isSubmitting

  return (
    <Components.Wrapper>
      <Components.MainContainer>
        <Link to={`/residents/${id}/initial-assessments`}>
          <LeftOutlined /> Back
        </Link>
        <Components.QuoteBox>
          <ResidentQuoteBox
            resident={resident}
            text={getResidentBoxText(carePlanNominative)}
          />
        </Components.QuoteBox>
        <Components.FormContainer>
          <InitialAssessmentCompleteForm
            {...({
              features,
              preferredPeopleNominative
            } as Record<string, unknown>)}
          />
        </Components.FormContainer>
      </Components.MainContainer>
      <Components.Footer className="lmc-fixed-footer">
        <Components.FinaliseButton
          id="finalise-initial-assessment"
          disabled={disabled}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          variant="primary"
        >
          Finalise Initial Assessment
        </Components.FinaliseButton>
      </Components.Footer>
    </Components.Wrapper>
  )
}

export default ResidentInitialAssessmentCompleteScreen
